import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { SINGLE_SELECT_FILTERS } from 'constants/appConstants';
import { evFacetClick } from 'events/search';
import { head } from 'helpers/lodashReplacement';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import marketplace from 'cfg/marketplace.json';
import { FILTERS_RE } from 'common/regex';
import { trackEvent } from 'helpers/analytics';
import { track } from 'apis/amethyst';

import styles from 'styles/components/search/compactSingleSelects.scss';

const { search: { maxPills } } = marketplace;

export class CompactSingleSelects extends Component {
  static displayName = 'CompactSingleSelects';

  constructor(props) {
    super(props);
    this.onCrumbEnter = this.onCrumbEnter.bind(this);
    this.onCrumbLeave = this.onCrumbLeave.bind(this);
    this.onScrollBreadcrumbs = this.onScrollBreadcrumbs.bind(this);
    this.scrollBreadCrumbs = this.scrollBreadCrumbs.bind(this);
    this.shouldShowScrollButtons = this.shouldShowScrollButtons.bind(this);
    this.onPillClick = this.onPillClick.bind(this);
  }

  state = {
    scrollingBreadcrumbs: false
  };

  onCrumbEnter(e) {
    let node = e.target.parentNode;
    const isSingleSelect = JSON.parse(e.target.getAttribute('data-singleselect'));
    while (node && isSingleSelect && node.nextSibling) {
      node.nextSibling.firstChild.className = styles.crossed;
      node = node.nextSibling;
    }
  }

  onCrumbLeave(e) {
    let node = e.target.parentNode;
    while (node && node.nextSibling) {
      node.nextSibling.firstChild.classList.remove(styles.crossed);
      node = node.nextSibling;
    }
  }

  makeBreadcrumbClickHandler = crumb => {
    const { router } = this.context;
    const { removePersonalizedSize, filters } = this.props;
    return e => {
      e.preventDefault();
      if (filters?.personalizedSize?.sizes?.includes(crumb.name)) {
        removePersonalizedSize();
      }
      router.forceBrowserPush(crumb.removeUrl);
      track(() => ([
        evFacetClick, {
          breadcrumbRemove: crumb,
          facetClickSource: 'FACET_PILLS',
          selected: false,
          deselected: true
        }
      ]));
      return false;
    };
  };

  makeCrumbContent(crumb, isSingleSelect) {
    const { testId } = this.context;
    const crumbTitle = `Remove ${crumb.name} filter`;
    return (
      <a // eslint-disable-line jsx-a11y/anchor-is-valid
        href={!FILTERS_RE.test(crumb.removeUrl) ? crumb.removeUrl : ''}
        aria-label={crumbTitle}
        data-singleselect={!!isSingleSelect}
        onFocus={this.onCrumbEnter}
        onBlur={this.onCrumbLeave}
        onMouseEnter={this.onCrumbEnter}
        onMouseLeave={this.onCrumbLeave}
        tabIndex={0}
        onClick={this.makeBreadcrumbClickHandler(crumb)}
        data-test-id={testId('singleSelectBreadcrumb')}>
        {crumb.name}
      </a>
    );
  }

  onPillClick(crumb, index) {
    const { handlePillClick } = this.props;
    const { breadcrumbWrapper } = this;
    breadcrumbWrapper.scrollLeft = 0;
    handlePillClick(crumb, index);
    trackEvent('TE_SEARCH_PILLCLICKED', `${crumb.name}:${crumb.value}`);
    track(() => ([
      evFacetClick, { crumb, facetClickSource: 'FACET_PILLS', selected: true, deselected: false }
    ]));
  }

  makeCompactSingleSelects() {
    const { filters, compact } = this.props;
    const { testId } = this.context;
    const breadCrumbs = filters && filters.breadcrumbs && filters.breadcrumbs.map(crumb => {
      const { name, removeUrl } = crumb;
      const isSingleSelect = Object.keys(filters.selected.singleSelects).filter(e => filters.selected.singleSelects[e][0] === name);
      return (
        <li key={`${removeUrl}-${name}`} className={cn({ [styles.onSaleFacet]: name === 'On Sale', [styles.defaultTerm]: name === 'Search' })}>
          {this.makeCrumbContent(crumb, isSingleSelect[0])}
        </li>
      );
    });

    const pills = filters.pills && filters.pills.recommendations.map((crumb, i) => {
      if (i < (maxPills + 1)) {
        return (
          <li key={`${crumb.value}-${crumb.name}`} className={styles.pills}>
            <button
              type="button"
              key={i}
              onClick={() => this.onPillClick(crumb, i)}
              aria-label={`Select ${crumb.value} filter`}>
              {crumb.value}
            </button>
          </li>
        );
      }
    });

    if (!compact && (filters.breadcrumbs || pills)) {
      return breadCrumbs.concat(pills);
    } else {
      return Object.keys(SINGLE_SELECT_FILTERS).map(k => {
        if (filters.selected.singleSelects[k]) {
          const crumbName = head(filters.selected.singleSelects[k]);
          const url = filters.breadcrumbs[crumbName];
          return (
            <li key={`${crumbName}-${url}`} className={styles.mSingleSelectCrumb}>
              <a
                href={url}
                aria-label={`Remove ${crumbName} filter`}
                data-selected-filter={k}
                onClick={this.handleBreadcrumbClick.bind(this)}
                data-test-id={testId('singleSelectBreadcrumb')}>
                {crumbName}
              </a>
            </li>
          );
        }
      }).concat(pills);
    }
  }

  handleBreadcrumbClick(event) {
    const { onV2CrumbSelect } = this.props;
    onV2CrumbSelect(event);
    event.preventDefault();
    return false;
  }

  scrollBreadCrumbs(isScrollingLeft, scrollTo) {
    const { breadcrumbWrapper } = this;
    this.setState({ scrollingBreadcrumbs: true });

    if ((isScrollingLeft && breadcrumbWrapper.scrollLeft > scrollTo) || (!isScrollingLeft && breadcrumbWrapper.scrollLeft < scrollTo)) {
      breadcrumbWrapper.scrollLeft += isScrollingLeft ? -20 : 20;
      setTimeout(this.scrollBreadCrumbs, 10, isScrollingLeft, scrollTo);
    } else {
      this.setState({ scrollingBreadcrumbs: false });
    }
  }

  onScrollBreadcrumbs(e) {
    const { breadcrumbWrapper: { scrollLeft, clientWidth, scrollWidth } } = this;
    const { scrollingBreadcrumbs } = this.state;

    if (scrollingBreadcrumbs) {
      e.preventDefault();
    } else {
      const maxScroll = scrollWidth - clientWidth;
      const isScrollingLeft = e.target.className === styles.leftScroll;
      let scrollTo;

      if (isScrollingLeft) {
        scrollTo = scrollLeft - clientWidth > 0 ? scrollLeft - clientWidth : 0;
      } else {
        scrollTo = scrollLeft + clientWidth < maxScroll ? scrollLeft + clientWidth : maxScroll;
      }

      this.scrollBreadCrumbs(isScrollingLeft, scrollTo);
    }
  }

  shouldShowScrollButtons() {
    const { breadcrumbWrapper, props: { hasAutoScroll } } = this;
    return hasAutoScroll && breadcrumbWrapper && (breadcrumbWrapper.scrollWidth - breadcrumbWrapper.clientWidth) > 0;
  }

  render() {
    const { filters, className, hasAutoScroll } = this.props;
    const { testId } = this.context;
    const shouldShowScrollButtons = this.shouldShowScrollButtons();
    let maxScroll = null;
    if (this.breadcrumbWrapper) {
      maxScroll = this.breadcrumbWrapper.scrollWidth - this.breadcrumbWrapper.clientWidth;
    }
    if (filters && (filters.breadcrumbs && filters.breadcrumbs.length > 0 || filters.pills && filters.pills.length > 0)) {
      return (
        <div className={cn(styles.compactSingleSelects, className || null, { [styles.hasAutoScroll]: hasAutoScroll, [styles.pillsCompactSingleSelects]: hasAutoScroll })} data-test-id={testId('breadcrumbScroll')}>
          <h2 className={styles.selectionText}>Your Selections</h2>
          {shouldShowScrollButtons && <button
            type="button"
            onMouseDown={this.onScrollBreadcrumbs}
            tabIndex="-1"
            disabled={this.breadcrumbWrapper && this.breadcrumbWrapper.scrollLeft <= 0}
            className={styles.leftScroll}
            data-test-id={testId('leftBreadcrumbScroll')}>Left Scroll</button>}
          <ul ref={e => this.breadcrumbWrapper = e}>
            {this.makeCompactSingleSelects()}
          </ul>
          {shouldShowScrollButtons && <button
            type="button"
            onMouseDown={this.onScrollBreadcrumbs}
            tabIndex="-1"
            disabled={this.breadcrumbWrapper && this.breadcrumbWrapper.scrollLeft === maxScroll}
            className={styles.rightScroll}
            data-test-id={testId('rightBreadcrumbScroll')}>Right Scroll</button>}
        </div>
      );
    }
    return null;
  }
}

CompactSingleSelects.contextTypes = {
  testId: PropTypes.func,
  router: PropTypes.object.isRequired
};

export default withErrorBoundary('CompactSingleSelects', CompactSingleSelects);
